import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {property} from 'lit/decorators/property.js';
import store from '../../../store';
import {pimText, pimTextDebug} from '../../../util/values';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional} from '../../../util/conditional';
import {
	selectDimensionHeight, selectDimensionWidth, selectDimensionDepth,
	selectWeight,
	selectWindsurfacearea,
	selectVariantCodeTypes, selectDimensionDiameter, selectSpcConfiguration
} from '../../../selectors';
import {saveTenderText} from '../../../slices/configurationSlice';

@customElement('new-light-tender-text')
export class NewLightTenderText extends connect(store)(LitElement) {

	static styles = css`
		p {
			font: var(--font-specsheet-print-light);
			line-height: 140%;
			margin: 0 0 10px 0;
		}

		.info {
			font: var(--font-specsheet-small-light);
		}

		.white {
			margin-left: 20px;
		}

		.white p {
			color: var(--color-grey4);
			font: var(--font-summary-paragraph);
			line-height: 22px;
		}

		@media print {
			p {
				font: var(--font-specsheet-print-light);
				line-height: 120%;
				margin: 0 0 1.5mm 0;
			}
		}

	`;

	@property({type: String})
	theme = '';

	tenderTextRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.lang = state.status.lang;
		this.state = state;
		this.debug = state.status.debug;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('copyToClipboard', () => this.copyToClipboard());
	}

	disconnectedCallback() {
		window.removeEventListener('copyToClipboard', () => this.copyToClipboard());
		super.disconnectedCallback();
	}
	firstUpdated(changedProperties) {
		const tenderText = this.tenderTextRef.value.innerText;
		const cleanedText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		store.dispatch(saveTenderText({tenderText: cleanedText}));
	}

	copyToClipboard() {
		let tenderText = this.tenderTextRef.value.innerText;
		// clean lineBreaks and spaces
		tenderText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		navigator.clipboard.writeText(tenderText).then(
			() => {},
			(err) => {
				console.error('Async: Could not copy text: ', err);
			});
	}

	// shortcut for translated text
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		return html`
			<div ${ref(this.tenderTextRef)} class="${this.theme}">
				<p>
					${filterAndJoin([
						this.t('HS_HousingSize', 'FV.S'),
						this.t('LU_LuminaireModel.PoleLateral', 'DV.S'),
						this.t('HS_CircularBaseProfile', 'FV.S', null, ', '),
					], ' ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('root::LightEngine_1::LE_LightDistribution', 'FV.S'),
						this.t('root::LightEngine_1::LE_ColourTemperature', 'FV.S'),
						this.t('LE_DimmingLevel_1', 'FV.S', null, '', ' lm'),
						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV'),
						this.c('REDTAB.RETURN_EB_Wattage_Max') + ' W'
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_CircularBaseProfile', 'FV.L')
					], ' ')}
				</p>
				<p>
					<trans-label key="LE_LightEngineDescription" context="D"></trans-label>
					:
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV'),
						this.t('root::LightEngine_1::LE_OpticalVersion', 'FV.L'),
						this.t('HS_LightEmission.DarkSky', 'FV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV.L'),
						this.t('root::LightEngine_1::LE_ReflectorColour', 'FV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
					], ', ')}
				</p>

					<!--
					<p>
					${this.t('shared::Zhaga::HS_HousingZhaga', 'F', null, '', ':')}
					${this.t('shared::Zhaga::HS_HousingZhaga', 'FV.L')}
					</p>
					<p>
					${this.t('root::EBlock_1::EB_ControlNoWire', 'F', null, '', ':')}
					${filterAndJoin([
							this.t('root::EBlock_1::EB_ControlNoWire', 'FV.S'),
							this.t('root::EBlock_1::EB_ControlWire', 'FV.S'),
						], ' + ')}
					</p>
					<p>
					${this.t('root::EBlock_1::EB_ConstantLumenTechnology', 'F', null, '', ':')}
					${this.t('root::EBlock_1::EB_ConstantLumenTechnology', 'FV.S')}
					</p>
					-->

				<p>
					${filterAndJoin([
						this.t('shared::ColourCoating::HS_HousingColour', 'F', null, '', ': ') +
						this.t('shared::ColourCoating::HS_HousingColour', 'FV')
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('shared::ColourCoating::HS_HousingCoating', 'F', null, '', ': ') +
						this.t('shared::ColourCoating::HS_HousingCoating', 'FV')
					], ', ')}
				</p>


				<p>
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
					], ', ')}
				</p>
				<p>
					${filterAndJoin([
						this.t('HS_HousingDescription', 'D.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
						this.t('shared::HousingLuminaire::HS_HousingConnection', 'FV.S'),
						this.t('HS_Attachement.Avanza', 'DV.L')
					], ', ')}
				</p>

				<p>
					${this.t('LU_SurgeProtection', 'D')}
					${this.t('LU_SurgeProtection.10kV', 'DV.S', null, '', ',')}
					${this.t('LU_TechnicalRegion', 'D')}
					${this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', ',')}
					${this.t('LU_AmbientTemperature', 'D')}
					${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S')}
				</p>

				<p>
					${filterAndJoin([
						this.t('shared::HousingLuminaire::HS_HousingHeight', 'F') +
						this.t('shared::HousingLuminaire::HS_HousingHeight', 'FV', null, ' ',),
						this.t('LU_LuminaireWeight', 'D', null, ' ', ' ') +
						selectWeight(s) +
						this.t('LU_LuminaireWeightUnit', 'D', null, ' '),
						this.t('HS_WindSurfaceArea', 'D', null, '', ' ') +
						selectWindsurfacearea(s) +
						this.t('HS_WindSurfaceAreaUnit.M2', 'DV.S', null, ' ')
					], ', ')}
				</p>
				<p>
					${this.t('TE_Dimensions', 'D')}:
					${filterAndJoin([
						selectDimensionDiameter(s) ? (selectDimensionDiameter(s) + this.t('HS_HousingHeightUnit.mm', 'DV')) : null,
						selectDimensionDepth(s) ? (selectDimensionDepth(s) + this.t('HS_HousingHeightUnit.mm', 'DV')) : null,
						selectDimensionWidth(s) ? (selectDimensionWidth(s) + this.t('HS_HousingHeightUnit.mm', 'DV')) : null,
						selectDimensionHeight(s) ? (selectDimensionHeight(s) + this.t('HS_HousingHeightUnit.mm', 'DV')) : null,
					], ', ')}
				</p>
				<p>
					<!-- join -->
					${filterAndJoin([
						this.t('LU_IP.IP66', 'DV.S'),
						this.t('LU_IK.10', 'DV.S'),
						this.t('root::EBlock_1::EB_ElectricalProtectionClass', 'FV.S'),
						this.t('LU_ENEC_CertificateDescription.ENEC', 'DV.S'),
						this.t('LU_CE.CE', 'DV.S'),
					], ', ')}
				</p>
				<p>
					<br>
					${this.t('TE_Manufacturer', 'D', null, '', ':')}
					Selux
				</p>
				${when(this.state.status.features.variantCodes,
					() => html`
						${when(this.state.status.region === 'fra',
							() => html`
								<p class="info">
									${repeat(
										selectVariantCodeTypes(s).filter(variantCode => variantCode.id === 'FRA'),
										(variantCode) => html`Typ: ${variantCode.code}<br>`
									)}
								</p>
							`,
							() => html`
								<p class="info">
									${repeat(
										selectVariantCodeTypes(s).filter(variantCode => variantCode.id === 'GER'),
										(variantCode) => html`Typ: ${variantCode.code}<br>`
									)}
								</p>
							`
						)}
						<p class="info">
							${repeat(
								selectVariantCodeTypes(s).filter(variantCode => variantCode.id === 'RELUX'),
								(variantCode) => html`Relux: ${variantCode.code}<br>`
							)}
						</p>`
				)}
				<p>
					<trans-label .key="${this.c('DescriptiveAttributeReference.LU_Accessories')}" context="DV.L"></trans-label>
				</p>
			</div>
		`;
	}
}
